import { fetchJSON as fetchAPI } from './client';

export type HierarchyNode = { key: string; name: string; level: number; parent_key?: string; description?: string };

export type AtomicCapability = {
  key: string;
  name: string;
  domain: string;
  level: number;
  purpose: string;
  business_problems?: string[];
  kpis?: string[];
  dependencies?: string[];
  inputs?: string[];
  outputs?: string[];
  apis?: string[];
  events?: string[];
  ai_usage?: string;
  target_persona?: string;
  release_version?: string;
  deployable: boolean;
};

export type CapabilityTemplateField = {
  key: string;
  name: string;
  category: string;
  required: boolean;
  description?: string;
};

export type DependencyMatrixEntry = {
  key: string;
  capability_key: string;
  depends_on: string[];
  shared_services?: string[];
  shared_data?: string[];
  shared_ai?: string[];
  shared_events?: string[];
  shared_apis?: string[];
  shared_kpis?: string[];
  shared_ui?: string[];
  shared_workflows?: string[];
};

export type CapabilityOwnership = {
  key: string;
  capability_key: string;
  domain: string;
  business_owner: string;
  technical_owner: string;
  ai_owner?: string;
  data_owner?: string;
  kpi_owner?: string;
  release_owner?: string;
};

export type FeatureInventoryEntry = {
  key: string;
  feature: string;
  capability_key: string;
  domain: string;
  release?: string;
  status: string;
};

export type CapabilityDeliverable = {
  key: string;
  name: string;
  status: string;
  description?: string;
};

export type MCADashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  depends_on: string[];
  hierarchy_nodes: number;
  atomic_capabilities: number;
  listing_capabilities: number;
  advertising_capabilities: number;
  fba_capabilities: number;
  profit_capabilities: number;
  ai_capabilities: number;
  experiment_capabilities: number;
  executive_capabilities: number;
  dependency_entries: number;
  ownership_assignments: number;
  feature_inventory: number;
  deliverables: number;
  generated_at: string;
};

export type CapabilityValidation = {
  capability_key: string;
  valid: boolean;
  mission_defined: boolean;
  business_value: boolean;
  measurable_kpis: boolean;
  dependencies_mapped: boolean;
  apis_defined: boolean;
  events_defined: boolean;
  permissions_defined: boolean;
  deployable_unit: boolean;
  testing_strategy: boolean;
  release_version: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type MCABibleStatus = {
  bible_id: string;
  version: string;
  depends_on: string[];
  hierarchy_nodes: number;
  atomic_capabilities: number;
  listing_capabilities: number;
  advertising_capabilities: number;
  fba_capabilities: number;
  profit_capabilities: number;
  ai_capabilities: number;
  experiment_capabilities: number;
  executive_capabilities: number;
  dependency_entries: number;
  ownership_assignments: number;
  feature_inventory: number;
  deliverables: number;
  capability_driven: boolean;
  engineering_ready: boolean;
  status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const marketplaceCapabilityApi = {
  dashboard: () => fetchMarketIntel<MCADashboard>('/mca/dashboard'),
  hierarchy: () => fetchMarketIntel<{ hierarchy: HierarchyNode[] }>('/mca/hierarchy').then((r) => r.hierarchy),
  listingCapabilities: () => fetchMarketIntel<{ capabilities: AtomicCapability[] }>('/mca/listing-capabilities').then((r) => r.capabilities),
  advertisingCapabilities: () => fetchMarketIntel<{ capabilities: AtomicCapability[] }>('/mca/advertising-capabilities').then((r) => r.capabilities),
  fbaCapabilities: () => fetchMarketIntel<{ capabilities: AtomicCapability[] }>('/mca/fba-capabilities').then((r) => r.capabilities),
  profitCapabilities: () => fetchMarketIntel<{ capabilities: AtomicCapability[] }>('/mca/profit-capabilities').then((r) => r.capabilities),
  aiCapabilities: () => fetchMarketIntel<{ capabilities: AtomicCapability[] }>('/mca/ai-capabilities').then((r) => r.capabilities),
  experimentCapabilities: () => fetchMarketIntel<{ capabilities: AtomicCapability[] }>('/mca/experiment-capabilities').then((r) => r.capabilities),
  executiveCapabilities: () => fetchMarketIntel<{ capabilities: AtomicCapability[] }>('/mca/executive-capabilities').then((r) => r.capabilities),
  capabilityTemplate: () => fetchMarketIntel<{ template: CapabilityTemplateField[] }>('/mca/capability-template').then((r) => r.template),
  dependencyMatrix: () => fetchMarketIntel<{ dependencies: DependencyMatrixEntry[] }>('/mca/dependency-matrix').then((r) => r.dependencies),
  ownership: () => fetchMarketIntel<{ ownership: CapabilityOwnership[] }>('/mca/ownership').then((r) => r.ownership),
  featureInventory: () => fetchMarketIntel<{ features: FeatureInventoryEntry[] }>('/mca/feature-inventory').then((r) => r.features),
  deliverables: () => fetchMarketIntel<{ deliverables: CapabilityDeliverable[] }>('/mca/deliverables').then((r) => r.deliverables),
  validateCapability: (payload: { capability_key: string; attrs: Record<string, boolean> }) =>
    fetchMarketIntel<CapabilityValidation>('/mca/validate-capability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchMarketIntel<MCABibleStatus>('/mca/status'),
};
